import { useTranslation } from 'react-i18next';
import { Link } from '../../components/base/Link';
import { asset } from '../../lib/assets';
import Header from '../../components/feature/Header';
import BeforeAfter from '../../components/feature/BeforeAfter';
import LoopVideo from '../../components/feature/LoopVideo';
import Footer from '../../components/feature/Footer';

export default function VideoRedaction() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* 科技背景 - 保持所有动画效果不变 */}
        <div className="absolute inset-0 bg-brand-navy">
          <img src={asset('/images/real/market-after.webp')} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/75 to-blue-950/70"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 z-10">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('videoRedaction.heroTitle')}
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              {t('videoRedaction.heroSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* 处理前后对比：真实成片帧 */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('videoRedaction.demoTitle')}</h2>
            <p className="text-lg text-gray-600">{t('videoRedaction.demoSubtitle')}</p>
          </div>

          <div className="grid gap-10 lg:grid-cols-2 items-center">
            <div className="mx-auto w-full max-w-sm"><LoopVideo src={asset('/media/mall-entrance-20s.mp4')} poster={asset('/media/hero-mall-poster.jpg')} label={t('videoRedaction.demoTitle')} controls className="shadow-2xl" /></div>
            <div>
              <p className="text-lg text-gray-700 leading-relaxed">{t('videoRedaction.demoCaption')}</p>
              <ul className="mt-6 space-y-3 text-gray-600">
                <li className="flex items-start"><i className="ri-check-line text-brand-600 mr-2 mt-1"></i>{t('videoRedaction.demoFact1')}</li>
                <li className="flex items-start"><i className="ri-check-line text-brand-600 mr-2 mt-1"></i>{t('videoRedaction.demoFact2')}</li>
                <li className="flex items-start"><i className="ri-check-line text-brand-600 mr-2 mt-1"></i>{t('videoRedaction.demoFact3')}</li>
              </ul>
              <p className="mt-6 text-sm text-gray-500">{t('common.sourceNote')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 功能介绍 */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('videoRedaction.featuresTitle')}</h2>
            <p className="text-lg text-gray-600">{t('videoRedaction.featuresSubtitle')}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-follow-line text-2xl text-brand-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('videoRedaction.feature1Title')}</h3>
              <p className="text-gray-600">
                {t('videoRedaction.feature1Desc')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('videoRedaction.feature2Title')}</h3>
              <p className="text-gray-600">
                {t('videoRedaction.feature2Desc')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                <i className="ri-focus-2-line text-2xl text-brand-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('videoRedaction.feature3Title')}</h3>
              <p className="text-gray-600">
                {t('videoRedaction.feature3Desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 前后对比：夜市多人 */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('videoRedaction.compareTitle')}</h2>
            <p className="text-lg text-gray-600">{t('videoRedaction.compareSubtitle')}</p>
          </div>
          <BeforeAfter before={asset('/images/real/market-before.webp')} after={asset('/images/real/market-after.webp')} beforeLabel={t('videoRedaction.originalVideo')} afterLabel={t('videoRedaction.processedVideo')} alt={t('videoRedaction.compareTitle')} className="shadow-xl" />
        </div>
      </section>

      {/* 应用场景 */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('videoRedaction.scenariosTitle')}</h2>
            <p className="text-lg text-gray-600">{t('videoRedaction.scenariosSubtitle')}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-hospital-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('videoRedaction.scenario1Title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('videoRedaction.scenario1Desc')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-brand-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('videoRedaction.scenario2Title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('videoRedaction.scenario2Desc')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-group-line text-2xl text-brand-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('videoRedaction.scenario3Title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('videoRedaction.scenario3Desc')}
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-live-line text-2xl text-brand-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('videoRedaction.scenario4Title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('videoRedaction.scenario4Desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 技术规格 */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('videoRedaction.formatsTitle')}</h2>
            <p className="text-lg text-gray-600">{t('videoRedaction.formatsSubtitle')}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="text-center p-4 bg-white rounded-lg shadow">
              <i className="ri-video-line text-3xl text-brand-600 mb-2"></i>
              <div className="font-medium text-gray-900">{t('videoRedaction.mp4')}</div>
              <div className="text-sm text-gray-600">{t('videoRedaction.mp4Desc')}</div>
            </div>
            
            <div className="text-center p-4 bg-white rounded-lg shadow">
              <i className="ri-video-line text-3xl text-brand-600 mb-2"></i>
              <div className="font-medium text-gray-900">{t('videoRedaction.mov')}</div>
              <div className="text-sm text-gray-600">{t('videoRedaction.movDesc')}</div>
            </div>
            
            <div className="text-center p-4 bg-white rounded-lg shadow">
              <i className="ri-file-search-line text-3xl text-blue-600 mb-2"></i>
              <div className="font-medium text-gray-900">{t('videoRedaction.other')}</div>
              <div className="text-sm text-gray-600">{t('videoRedaction.otherDesc')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-navy">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {t('videoRedaction.ctaTitle')}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {t('videoRedaction.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://damaya.cn" className="bg-white text-brand-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              {t('videoRedaction.ctaButton1')}
            </a>
            <Link to="/pricing#contact" className="bg-brand-700 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-brand-800 transition-colors border border-brand-600 whitespace-nowrap cursor-pointer">
              {t('videoRedaction.ctaButton2')}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
